import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  DollarSign, TrendingUp, Calendar, Users, CheckCircle, BarChart3,
  Loader2, RefreshCw, Wallet, Banknote
} from 'lucide-react';

export const RevenueSection = ({ api }) => {
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [revenuePeriod, setRevenuePeriod] = useState(30);
  const [stripeBalance, setStripeBalance] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const fetchRevenueData = useCallback(async () => {
    setRevenueLoading(true);
    try {
      const response = await api.get(`/admin/revenue/overview?days=${revenuePeriod}`);
      setRevenueData(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
      const msg = error.response?.data?.detail || error.message || 'Unable to connect to payment service';
      toast.error(`Revenue: ${msg}`);
    } finally {
      setRevenueLoading(false);
    }
  }, [api, revenuePeriod]);

  const fetchRecentTransactions = async () => {
    try {
      const response = await api.get('/admin/revenue/transactions?limit=20');
      setRecentTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const fetchStripeBalance = async () => {
    try {
      const response = await api.get('/admin/revenue/balance');
      setStripeBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe balance:', error);
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const response = await api.get('/admin/revenue/payouts?limit=20');
      setPayoutHistory(response.data.payouts || []);
    } catch (error) {
      console.error('Failed to fetch payout history:', error);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    setWithdrawing(true);
    try {
      const response = await api.post('/admin/revenue/withdraw', { amount });
      toast.success(response.data.message);
      setWithdrawDialog(false);
      setWithdrawAmount('');
      fetchStripeBalance();
      fetchPayoutHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  useEffect(() => {
    fetchRevenueData();
    fetchRecentTransactions();
    fetchStripeBalance();
    fetchPayoutHistory();
  }, []);

  const availableBalance = revenueData?.available_balance || stripeBalance?.total_available || 0;
  const pendingBalance = revenueData?.pending_balance || stripeBalance?.total_pending || 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Revenue Analytics</h1>
          <p className="text-slate-600">Track your Stripe revenue and subscription performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={revenuePeriod.toString()} onValueChange={(v) => { setRevenuePeriod(parseInt(v)); }}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchRevenueData} disabled={revenueLoading}>
            <RefreshCw className={cn("w-4 h-4", revenueLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {revenueLoading && !revenueData ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : revenueData ? (
        <>
          {/* Stripe Balance & Withdrawal */}
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 border-0 text-white" data-testid="stripe-balance-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
                    <Wallet className="w-7 h-7 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 font-medium">Available Balance</p>
                    <p className="text-4xl font-bold" data-testid="available-balance">
                      ${availableBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="border-l border-slate-700 pl-6">
                    <p className="text-sm text-slate-400 font-medium">Pending</p>
                    <p className="text-2xl font-semibold text-amber-400" data-testid="pending-balance">
                      ${pendingBalance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
                <Button onClick={() => setWithdrawDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6" data-testid="withdraw-btn">
                  <Banknote className="w-4 h-4 mr-2" /> Withdraw Funds
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Revenue', value: `$${revenueData.total_revenue?.toLocaleString()}`, sub: `Last ${revenuePeriod} days`, icon: DollarSign, color: 'emerald' },
              { label: 'Net Revenue', value: `$${revenueData.net_revenue?.toLocaleString()}`, sub: 'After Stripe fees', icon: TrendingUp, color: 'blue' },
              { label: 'MRR', value: `$${revenueData.mrr?.toLocaleString()}`, sub: 'Monthly Recurring', icon: Calendar, color: 'purple' },
              { label: 'Successful Charges', value: revenueData.successful_charges, sub: `${revenueData.failed_charges} failed`, icon: CheckCircle, color: 'amber' },
            ].map((card) => (
              <Card key={card.label} className={`bg-gradient-to-br from-${card.color}-50 to-${card.color}-100 border-${card.color}-200`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm text-${card.color}-600 font-medium`}>{card.label}</p>
                      <p className={`text-3xl font-bold text-${card.color}-700`}>{card.value}</p>
                      <p className={`text-xs text-${card.color}-500`}>{card.sub}</p>
                    </div>
                    <div className={`w-12 h-12 bg-${card.color}-200 rounded-full flex items-center justify-center`}>
                      <card.icon className={`w-6 h-6 text-${card.color}-600`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Revenue by Plan & Active Subscriptions */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-500" /> Revenue by Plan</CardTitle>
                <CardDescription>Breakdown of revenue by subscription tier</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(revenueData.revenue_by_plan || {}).map(([plan, data]) => {
                    const colors = { free: 'bg-slate-200', basic: 'bg-blue-500', family: 'bg-emerald-500', premium: 'bg-amber-500', enterprise: 'bg-purple-500' };
                    const maxRevenue = Math.max(...Object.values(revenueData.revenue_by_plan || {}).map(d => d.revenue || 0), 1);
                    const percentage = ((data.revenue || 0) / maxRevenue) * 100;
                    return (
                      <div key={plan} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium capitalize">{plan}</span>
                          <span className="text-slate-600">${(data.revenue || 0).toLocaleString()} <span className="text-slate-400 ml-1">({data.count || 0} charges)</span></span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div className={cn("h-full rounded-full transition-all", colors[plan] || 'bg-slate-400')} style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-500" /> Active Subscriptions</CardTitle>
                <CardDescription>Current active subscribers by plan</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(revenueData.active_subscriptions || {}).map(([plan, count]) => {
                    const colors = { free: { bg: 'bg-slate-100', text: 'text-slate-600' }, basic: { bg: 'bg-blue-100', text: 'text-blue-600' }, family: { bg: 'bg-emerald-100', text: 'text-emerald-600' }, premium: { bg: 'bg-amber-100', text: 'text-amber-600' }, enterprise: { bg: 'bg-purple-100', text: 'text-purple-600' } };
                    const color = colors[plan] || colors.free;
                    return (
                      <div key={plan} className={cn("p-3 rounded-lg", color.bg)}>
                        <p className={cn("text-2xl font-bold", color.text)}>{count}</p>
                        <p className="text-sm text-slate-600 capitalize">{plan}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Daily Revenue Chart */}
          {revenueData.daily_revenue && revenueData.daily_revenue.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Daily Revenue (Last 7 Days)</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 h-32">
                  {revenueData.daily_revenue.map((day, idx) => {
                    const maxDaily = Math.max(...revenueData.daily_revenue.map(d => d.revenue), 1);
                    const height = (day.revenue / maxDaily) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600" style={{ height: `${Math.max(height, 5)}%` }} title={`$${day.revenue}`} />
                        <span className="text-xs text-slate-500">{day.date.slice(5)}</span>
                        <span className="text-xs font-medium">${day.revenue}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Transactions */}
          <Card>
            <CardHeader><CardTitle>Recent Transactions</CardTitle><CardDescription>Latest charges from Stripe</CardDescription></CardHeader>
            <CardContent>
              {recentTransactions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Plan</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentTransactions.map((tx) => (
                      <TableRow key={tx.id}>
                        <TableCell className="text-sm">{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-sm">{tx.customer_email || 'N/A'}</TableCell>
                        <TableCell><span className={cn("px-2 py-1 rounded text-xs font-medium capitalize", tx.plan === 'basic' ? "bg-blue-100 text-blue-700" : tx.plan === 'enterprise' ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-700")}>{tx.plan}</span></TableCell>
                        <TableCell className="font-medium">${tx.amount.toFixed(2)} {tx.currency}{tx.refunded && <span className="text-red-500 text-xs ml-1">(-${tx.refund_amount.toFixed(2)} refunded)</span>}</TableCell>
                        <TableCell><span className={cn("px-2 py-1 rounded text-xs", tx.status === 'succeeded' ? "bg-green-100 text-green-700" : tx.status === 'failed' ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700")}>{tx.status}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">No transactions found</div>
              )}
            </CardContent>
          </Card>

          {/* Payout History */}
          <Card data-testid="payout-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-emerald-500" /> Payout History</CardTitle>
              <CardDescription>Past withdrawals to your bank account</CardDescription>
            </CardHeader>
            <CardContent>
              {payoutHistory.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Date</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Arrival Date</TableHead><TableHead>Method</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {payoutHistory.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-sm">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="font-medium">${p.amount.toFixed(2)} {p.currency}</TableCell>
                        <TableCell>
                          <span className={cn("px-2 py-1 rounded text-xs font-medium", p.status === 'paid' ? "bg-green-100 text-green-700" : p.status === 'pending' ? "bg-amber-100 text-amber-700" : p.status === 'in_transit' ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700")}>{p.status}</span>
                        </TableCell>
                        <TableCell className="text-sm">{p.arrival_date ? new Date(p.arrival_date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell className="text-sm capitalize">{p.method || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">No payouts yet. Withdraw funds to see them here.</div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="p-8 text-center">
          <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Unable to load revenue data</p>
          <Button variant="outline" onClick={fetchRevenueData} className="mt-4">Try Again</Button>
        </Card>
      )}

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawDialog} onOpenChange={setWithdrawDialog}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw Funds</DialogTitle>
            <DialogDescription>Transfer available balance to your connected bank account via Stripe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-4 rounded-lg bg-slate-50 border">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Available Balance</span>
                <span className="text-xl font-bold text-emerald-600" data-testid="withdraw-available">${availableBalance.toFixed(2)}</span>
              </div>
            </div>
            <div>
              <Label>Withdrawal Amount ($)</Label>
              <Input type="number" step="0.01" min="0.01" placeholder="Enter amount" value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} className="mt-1" data-testid="withdraw-amount-input" />
            </div>
            <Button variant="link" className="text-xs text-emerald-600 p-0 h-auto" onClick={() => setWithdrawAmount(availableBalance.toString())} data-testid="withdraw-max-btn">Withdraw max</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawDialog(false)}>Cancel</Button>
            <Button onClick={handleWithdraw} disabled={withdrawing || !withdrawAmount || parseFloat(withdrawAmount) <= 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="withdraw-confirm-btn">
              {withdrawing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm Withdrawal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
